/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  IconButton,
  Tooltip,
  Popover,
  PopoverActions,
  Badge,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../../reducers';
import { ChatToolbarProps } from './types';
import { UploadFileComponent } from '../../../common/components/ai-assistant/Compose/UploadFile';
import { NotebookPen } from 'lucide-react';
import { marked } from 'marked';

const ToolbarContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1, 2),
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
}));

const ChatToolbar: React.FC<ChatToolbarProps> = ({ disabled }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const messages = useSelector(
    (state: RootState) => state.workflow.currentWorkflow.messages,
  );
  const referenceState = useSelector((state: RootState) => state.reference);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverActions = React.useRef<PopoverActions>();

  const handleOpen = () => {
    setAnchorEl(buttonRef.current);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <ToolbarContainer>
      <Tooltip title={t('chat.uploadFile')} placement="top" arrow>
        <Badge
          badgeContent={referenceState.selectedFileIds.length}
          color="primary"
        >
          <span>
            <IconButton
              ref={buttonRef}
              color="primary"
              onClick={handleOpen}
              disabled={disabled}
              size="small"
            >
              <AttachFileIcon />
            </IconButton>
          </span>
        </Badge>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        action={popoverActions}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            width: 400,
            p: 2,
            transform: 'translate(100%, -100%)',
            mb: 1,
          },
        }}
      >
        <UploadFileComponent
          onFilesLoad={() => {
            if (popoverActions.current) {
              popoverActions.current.updatePosition();
            }
          }}
        />
      </Popover>
      <Box sx={{ flex: 1 }} />
      <Tooltip title={t('chat.openInWriter')} placement="top" arrow>
        <span>
          <IconButton
            color="primary"
            onClick={async () => {
              const lastAiMessage = messages
                .filter((msg) => msg.sender === 'ai' && msg.status === 'sent')
                .pop();
              if (lastAiMessage) {
                navigate('/articles/new', {
                  state: {
                    content: await marked(
                      lastAiMessage.content.split('</think>').pop(),
                    ),
                  },
                });
              }
            }}
            disabled={
              disabled ||
              !messages.some(
                (msg) => msg.sender === 'ai' && msg.status === 'sent',
              )
            }
            size="small"
          >
            <NotebookPen />
          </IconButton>
        </span>
      </Tooltip>
    </ToolbarContainer>
  );
};

export default ChatToolbar;
